/**
 * „Nagrania" — every recording of the client, newest first, grouped by month (canon FV6-01 and
 * FV6-02). A movement chosen in the list narrows it to that movement's recordings.
 *
 * Two reads feed it. Every recording comes from the trainer's collection narrowed to this client
 * (`GET /trainer/clips?customerId=`, paged, in the backend's order); one movement comes from its own
 * timeline (at most ten, the dock's and the player's cache entry). The months and tiles are
 * `entities/exercise-clip`'s `clipMonths`, shared with `/nagrania`.
 *
 * A tile names its set with `filmedSetWords` over the movement's history WHERE IT IS LOADED, on both
 * reads, so one clip reads the same whichever of the two drew it — „Seria 3 z 4" once the history is
 * loaded, the logger's own „Seria 3" before.
 */

import {
  byNewestClip,
  clipMonths,
  type ClipEntry,
  type ClipMonth,
  type CollectionClip,
} from '~/entities/exercise-clip';
import { filmedSetWords, type LiftSession } from '~/entities/lift-history';
import type { Translate } from '~/shared/lib/localeTranslate';

export interface FormaGallery {
  readonly count: number;
  readonly months: readonly ClipMonth[];
  /** „Nie udało się wczytać nagrań: Wyciskanie leżąc." while the chosen movement's read failed. */
  readonly missing: string | null;
}

export type GallerySource =
  | {
      /** Every recording of the client, the pages landed so far, newest first. */
      readonly kind: 'client';
      readonly clips: readonly CollectionClip[];
      /** The client's count from the collection's summary, or `null` where it names no such row. */
      readonly count: number | null;
    }
  | {
      /** One movement's timeline; `clips` is `null` when it could not be read. */
      readonly kind: 'movement';
      readonly name: string;
      readonly clips: readonly ClipEntry[] | null;
    };

export interface GalleryInput {
  readonly source: GallerySource;
  /** The histories already loaded, by movement. */
  readonly sessions: ReadonlyMap<number, readonly LiftSession[]>;
  readonly viewerUserId: string | null;
}

export interface GalleryDeps {
  readonly t: Translate;
  readonly locale: string;
}

export function buildGallery(input: GalleryInput, { t, locale }: GalleryDeps): FormaGallery {
  const { source, sessions } = input;
  const words = { t, locale };
  const clips =
    source.kind === 'client'
      ? source.clips.map((clip) => ({ clip, exerciseName: clip.exerciseName }))
      : [...(source.clips ?? [])].sort(byNewestClip).map((clip) => ({ clip, exerciseName: source.name }));

  return {
    count: source.kind === 'client' ? (source.count ?? clips.length) : clips.length,
    months: clipMonths(
      clips.map(({ clip, exerciseName }) => ({
        clip,
        exerciseName,
        setWords: filmedSetWords(clip.anchor, sessions.get(clip.exerciseId), words),
        client: null,
      })),
      { t, locale, viewerUserId: input.viewerUserId, author: true },
    ),
    missing:
      source.kind === 'movement' && source.clips === null
        ? t('web:forma.gallery.missing', { names: source.name })
        : null,
  };
}
